/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';
import { ScannedActionsSubject, Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { Overlay } from '@angular/cdk/overlay';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { routes } from './app-routing.module';
import { AppUtilityService } from '../shared/services/app-utility.service';

import { selectAppState, selectIsSuperUser, selectSampleDataLoading, selectSampleDataStatus } from './state/selectors';
import { LoadStudyDialogComponent } from '../shared/components/load-study-dialog/load-study-dialog.component';
import { CommonDialogComponent } from '../shared/components/common-dialog/common-dialog.component';
import { SaveStudyDialogComponent } from '../shared/components/save-dialog/save-study-dialog.component';
import { Study } from '../shared/models/study.model';
import { resetSelectedStudyAndPanel, runCalculation, runCalculationError, runCalculationSuccess, runGetCalculationCsv, setAdjustedCalLots, setProcessingStatus } from './state/actions';
import { CommitStudyDialogComponent } from '../shared/components/commit-dialog/commit-dialog.component';
import { CalculationRequest } from '../shared/models/calculate.model';
import { StatusSummaryDialogComponent } from '../shared/components/status-summary-dialog/status-summary-dialog.component';

import { MsalService } from '@azure/msal-angular';
import { compareArrays, getValueAssignmentData } from '../shared/utils/helper';
import { isMatchKitRunType } from '../shared/utils/run-type.util';
import { AdjustedCalFormat, CalAdjustmentCriteria } from '../shared/models/result.model';
import { ReportContentItem } from '../shared/models/run.model';
import { ProcessingActivity } from './state/store';
import { AssayNavigationWatcherService } from './state/assay-navigation-watcher.service';
import { SampleDataLoadingService } from '../shared/services/sample-data-loading.service';
import { SampleDataLoadingDialogComponent, SAMPLE_DATA_LOADING_DIALOG_ID } from '../shared/components/sample-data-loading-dialog/sample-data-loading-dialog.component';
import { ExportWorkbookStatusService } from '../shared/services/export-workbook-status.service';
import { ReportDownloadStatusService } from '../shared/services/report-download-status.service';
import { ApiLogService } from '../shared/services/api-log.service';


/**
 * This is App component which handles layout and routing
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  routes: Routes = []; /** List of routes available in the application, dynamically filtered based on study type. */
  title = 'bpx-app'; /** Application title */
  appState$ = this.store.select(selectAppState); /** Observable for the application state from NgRx store. */
  selectedStudy!: Study; /** Currently selected study, used across all study-related operations. */
  panelName!: string; /** Name of the selected panel for the study. */
  loggedInUser!: string; /** Full name of the currently logged-in user. */
  appVersion: string = ''; /** Current BPX application version reported by the API. */
  isCalculateEnabled = false; /** Flag to control whether calculation button to be enabled or not. */
  isCalculateAdjustedEnabled = false; /** Flag to indicate if adjusted calculation is allowed based on changes to calibration lots. */
  adjustedCalibratorVisited = false;
  isProcessing = false; /** Indicates whether a calculation is currently being processed. */
  @ViewChild('sidenav') sideNav!: MatSidenav; /** Reference to the side navigation panel. */
  firstName: string = ''; /**First name of the logged-in user */
  lastName: string = ''; /** Last name of the logged-in user (parsed from `loggedInUser`). */
  calAdjustmentCriteria: CalAdjustmentCriteria[] = []; /** Criteria used for calibration adjustment, part of calculation logic. */
  reportContentItemsData!: ReportContentItem[]; /** Report content items */
  adjustedCalLotData !: AdjustedCalFormat; /** Adjsuted calculation data and original data */
  calculationStatus: string = ''; /** Status string representing the current state of the calculation (e.g. PASS, FAIL, NA). */
  processingActivity?: ProcessingActivity; /** Which long-running operation the header spinner is reporting on. */
  loadStudyError: string | null = null; /** Error message shown in header when load study fails. */
  isStudyCommitted = false;
  isSuperUser$ = this.store.select(selectIsSuperUser);
  isAuthenticated$ = this.appUtilityService.isAuthenticated(); /** Created once so the template's async pipes keep a single subscription instead of resubscribing every change detection. */
  private readonly menuRouteVisibility = new Map<Route, Observable<boolean>>(); /** Per-route visibility streams for the sidebar, cached for the same reason as `isAuthenticated$`. */
  protected destroy$ = new Subject<boolean>(); /** Subject used to signal component teardown and unsubscribe from observables. */
  disabledRoutes: string[] = ['Assays', 'Calculations', 'Samples', 'Calibration', 'Controls', 'Assign', 'Run Status', 'C of A'];
  showSampleDataBanner$: Observable<boolean> = of(false);
  showWorkbookExportBanner$: Observable<boolean> = of(false);
  showReportDownloadBanner$: Observable<boolean> = of(false);
  private sampleDataDialogRef: MatDialogRef<SampleDataLoadingDialogComponent> | null = null;
  private sampleDataLoadStarted = false;
  private readonly assignmentRunTypes = new Set([
    'Calibrator Assignment',
    'CalibratorAdjustment',
    'Calibrator Adjustment',
    'calibrator adjustment',
    'Control Assignment',
    'ControlAssignment',
    'control assignment'
  ]);

  /**
    * Constructs the AppComponent and injects all required services.
    * 
    * @param appUtilityService Shared application utility service
    * @param actions NgRx action stream for listening to dispatched actions
    * @param store NgRx store for accessing application state
    * @param dialog Material dialog service for showing modal dialogs
    * @param overlay CDK overlay service
    * @param router Angular router for navigation
    * @param authService MSAL authentication service for managing user sessions
    * @param assayNavWatcher Service to track assay-specific navigation logic
    */
  constructor(
    public appUtilityService: AppUtilityService,
    private actions: ScannedActionsSubject,
    private store: Store,
    public dialog: MatDialog,
    public overlay: Overlay,
    private router: Router,
    private authService: MsalService,
    private readonly assayNavWatcher: AssayNavigationWatcherService,
    private readonly sampleDataLoadingService: SampleDataLoadingService,
    private readonly exportWorkbookStatus: ExportWorkbookStatusService,
    private readonly reportDownloadStatus: ReportDownloadStatusService,
    private readonly apiLogService: ApiLogService
  ) { }

  /**
   * Initializes component state by subscribing to application state changes.
   * Extracts user and study-related data, evaluates calculation flags,
   * and configures route visibility.
   */
  ngOnInit(): void {
    (window as unknown as { bpxApiLogs?: ApiLogService }).bpxApiLogs = this.apiLogService;
    console.info('[BPX] API logs available at window.bpxApiLogs');
    this.showSampleDataBanner$ = combineLatest([
      this.store.select(selectSampleDataLoading),
      this.sampleDataLoadingService.dismissed$
    ]).pipe(
      map(([isLoading, dismissed]) => isLoading && dismissed)
    );
    this.showWorkbookExportBanner$ = this.exportWorkbookStatus.exporting$;
    this.showReportDownloadBanner$ = this.reportDownloadStatus.generating$;

    this.store.select(selectSampleDataStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        if (status.sampleDataLoading && !this.sampleDataLoadStarted) {
          this.sampleDataLoadStarted = true;
          this.sampleDataLoadingService.resetDismissal();
          this.openSampleDataDialog(true);
        }

        if (status.sampleDataLoading) {
          this.openSampleDataDialog();
        } else if (this.sampleDataLoadStarted) {
          this.closeSampleDataDialog();
          this.sampleDataLoadStarted = false;
          this.sampleDataLoadingService.resetDismissal();
        }
      });

    this.appState$.pipe(
      filter(appState => !!appState),
      takeUntil(this.destroy$)
    ).subscribe(appState => {
      this.selectedStudy = appState.selectedStudy;
      this.panelName = appState.panelName;
      this.isCalculateEnabled = appState.isCalculateEnabled;
      this.loggedInUser = appState.loggedInUser;
      this.appVersion = appState.appVersion;
      const userNameSplit = this.loggedInUser.split(' ');
      this.firstName = userNameSplit[0];
      this.lastName = userNameSplit[1] ?? '';
      this.calculationStatus = appState.calculationStatus;
      this.loadStudyError = appState.loadStudyError ?? null;
      this.calAdjustmentCriteria = appState.calAdjustmentCriteria;
      this.adjustedCalLotData = appState.selectedStudy.adjustedCalLots;
      this.reportContentItemsData = appState.selectedStudy.reportContentItems.reportContentItems;
      this.routesSetting();
      this.isCalculateAdjustedEnabled = compareArrays(this.adjustedCalLotData.originalCalLot, this.adjustedCalLotData.adjustedCalLot);
      this.adjustedCalibratorVisited = appState.adjustedCalibratorVisited;
      this.isProcessing = appState.isProcessing;
      this.processingActivity = appState.processingActivity;
      this.isStudyCommitted = appState.selectedStudy.isCommitted
        ?? (appState.selectedStudy.auditTrail ?? []).some(entry =>
          (entry.comment ?? '').toLowerCase().startsWith('study committed')
        );
    });

    this.authService.instance.enableAccountStorageEvents(); // This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

  }

  /**
  * Dynamically filters available routes based on the run type of the selected study.
  * Assignment-related routes are removed unless the run type is an assignment type, and the
  * C of A route is removed unless the run type is Calibrator Match Kit or Control Match Kit.
  * Routes removed here are not rendered in the sidebar at all, rather than being rendered in a
  * disabled state.
  */
  routesSetting() {
    const selectedPhaseTypeText = this.selectedStudy.runType;
    const isAssignmentRun = this.isAssignmentRunType(selectedPhaseTypeText);
    const isMatchKitRun = isMatchKitRunType(selectedPhaseTypeText);

    this.routes = routes.filter(route => {
      if (route.path === 'assignment') {
        return isAssignmentRun;
      }
      if (route.path === 'coa') {
        return isMatchKitRun;
      }
      return true;
    });
  }

  private static readonly processingLabels: Record<ProcessingActivity, string> = {
    calculating: 'Calculating',
    loading: 'Loading',
    saving: 'Saving',
    exporting: 'Exporting',
    importingCalibratorStudy: 'Importing Calibrator Match Kit Study'
  };

  /**
   * Wording for the header's progress chip. The generic fallback covers any future dispatcher that
   * raises `isProcessing` without naming what it is doing.
   */
  get processingLabel(): string {
    return this.processingActivity
      ? AppComponent.processingLabels[this.processingActivity]
      : 'Working';
  }

  isCalibratorAdjustmentRunType(): boolean {
    const runType = this.selectedStudy?.runType ?? '';
    const normalized = runType.trim().replace(/[\s_-]+/g, '').toLowerCase();
    return normalized === 'calibratoradjustment';
  }

  shouldDisableNormalCalculate(): boolean {
    if (!this.isCalibratorAdjustmentRunType()) {
      return false;
    }
    const wouldEnableAdjusted = this.isCalculateEnabled && !this.isProcessing && !this.isCalculateAdjustedEnabled;
    return wouldEnableAdjusted;
  }

  shouldDisableAdjustedCalculate(): boolean {
    if (!this.adjustedCalibratorVisited) {
      return true;
    }
    return this.isCalculateAdjustedEnabled;
  }

  /**
   * isRouteVisible Function on load will show only "Run Data", "Run Info" and "Logout"
   * @param route The Angular route to evaluate.
   * @returns An Observable that emits `true` if the route should be visible, otherwise `false`.
   */
  public isRouteVisible(route: Route): Observable<boolean> {
    // The Studies logbook is a cross-study overview, so it stays reachable with no study loaded.
    const allowedRoutes = ['studies', 'run-data/file-upload', 'run', 'logout'];

    return this.isRouteEnabled(route).pipe(
      map((isStudyLoaded: boolean) => {
        if (!isStudyLoaded) return false;
        // Read on each emission rather than at call time, so a cached stream follows study changes.
        const selectedPhaseTypeText = this.selectedStudy?.runType;
        if (this.selectedStudy.calculationResponse) {
          if (
            route.path === 'assignment' &&
            !this.isAssignmentRunType(selectedPhaseTypeText)
          ) {
            return false;
          }
          if (route.path === 'coa' && !isMatchKitRunType(selectedPhaseTypeText)) {
            return false;
          }
          return true;
        }

        return route.path !== undefined && allowedRoutes.includes(route.path);
      })
    );
  }

  /**
   * Sidebar binding for `isRouteVisible`: returns the same stream for a route on every call, so the
   * template's async pipe is not handed a new observable (and a new subscription) each change detection.
   * @param route The Angular route to evaluate.
   */
  public menuRouteVisible(route: Route): Observable<boolean> {
    let visible$ = this.menuRouteVisibility.get(route);
    if (!visible$) {
      visible$ = this.isRouteVisible(route);
      this.menuRouteVisibility.set(route, visible$);
    }
    return visible$;
  }

  private isAssignmentRunType(runType?: string | null): boolean {
    if (!runType) {
      return false;
    }
    const normalized = runType.trim();
    if (!normalized) {
      return false;
    }
    if (this.assignmentRunTypes.has(normalized)) {
      return true;
    }
    const normalizedLower = normalized.toLowerCase().replace(/\s+/g, '');
    return normalizedLower === 'calibratorassignment'
      || normalizedLower === 'controlassignment'
      || normalizedLower === 'calibratoradjustment';
  }

  /**
   * isRouteEnabled
   * @param route
   * @returns
   */
  public isRouteEnabled(route: Route): Observable<boolean> {
    return (route.data && !!route.data['requiresStudy']) ? this.appUtilityService.isStudyLoaded() : of(true);
  }

  /**
   * isPrintMode Function is used to print the page
   * @returns redirects to print page
   */
  public isPrintMode(): boolean {
    return this.router.url.includes('print');
  }

  /**
   * The sign-in screen draws its own full-bleed layout, so the shell's toolbar and content gutter
   * come off for it. Deliberately not matched on `/`: the app sits there only for the instant
   * before the wildcard route redirects to `/login`.
   */
  public isAuthScreen(): boolean {
    const url = (this.router.url ?? '').split('?')[0];
    return url === '/login' || url === '/logout';
  }

  /**
   * Funtion called when "Load Study" is clicked
   * @returns a dialog box
   */
  loadStudy() {
    const dialogRef = this.dialog.open(
      LoadStudyDialogComponent,
      {
        autoFocus: false,
        panelClass: 'bpx-dialog-container',
        scrollStrategy: this.overlay.scrollStrategies.noop(),
        disableClose: true,
        width: 'auto'
      }
    );
    return dialogRef;
  }

  /**
   * Function called when "New Study" is clicked
   * @returns a dialog box
   */
  loadNewStudy() {
    const dialogRef = this.dialog.open(
      CommonDialogComponent,
      {
        autoFocus: false,
        panelClass: 'bpx-dialog-container',
        scrollStrategy: this.overlay.scrollStrategies.noop(),
        disableClose: true,
        data: {
          title: "CONFIRM",
          message: "Are you sure you want to exit the current study and begin a new study?",
          okText: 'OK',
          dismissText: 'Dismiss',
          okAction: () => {
            this.store.dispatch(resetSelectedStudyAndPanel());
            if (window.location.href.includes('file-upload')) {
              const tabHeaders = document.querySelectorAll('.mdc-tab__text-label');
              tabHeaders.forEach((header: Element) => {
                const tabLabel = header.textContent?.trim();
                if (tabLabel === "Instrument Data") {
                  (header as HTMLElement).click();
                }
              });
            } else {
              this.router.navigate(['/run-data/file-upload/']);
            }
          },
          dismissAction: () => { }
        }
      }
    );
    return dialogRef;
  }

  /**
   * Function called when "Save" button is clicked
   */
  saveStudy() {
    let selectedStudy: Study | null;

    this.appState$.pipe(
      filter(appState => !!appState),
      take(1)
    ).subscribe(appState => {
      selectedStudy = appState.selectedStudy;

      const dialogRef = this.dialog.open(
        SaveStudyDialogComponent,
        {
          autoFocus: false,
          panelClass: ['bpx-dialog-container', 'save-study-dialog-container'],
          scrollStrategy: this.overlay.scrollStrategies.noop(),
          disableClose: true,
          data: {
            study: selectedStudy
          },
          width: '600px'
        }
      );
      return dialogRef;
    });
  }

  /**
   * Function called when "Commit" button is clicked
   */
  commitStudy() {
    let selectedStudy: Study | null;

    this.appState$.pipe(
      filter(appState => !!appState),
      take(1)
    ).subscribe(appState => {
      selectedStudy = appState.selectedStudy;

      const dialogRef = this.dialog.open(
        CommitStudyDialogComponent,
        {
          autoFocus: false,
          panelClass: ['bpx-dialog-container', 'save-study-dialog-container'],
          scrollStrategy: this.overlay.scrollStrategies.noop(),
          disableClose: true,
          data: {
            study: selectedStudy
          },
          width: '600px'
        }
      );
      return dialogRef;
    });
  }

  /**
   * Function called when "Calculate" or "Calculate wADJ" button is clicked
   * @param label to know if button clicked is from "Calculate" or "Calculate wADJ"
   */
  calculate(label: string) {
    this.store.dispatch(setProcessingStatus({
      isProcessing: true,
      activity: label === 'GetCsv' ? 'exporting' : 'calculating'
    }));

    const isAdjustedCalculation = label === 'CalculateWithAdjustment';
    // Not gated on calAdjustmentCriteria: those are recomputed from the last calculation response, so
    // a second wADJ run skipped this and sent whatever stale value assignments the store held.
    if (isAdjustedCalculation) {
      const normalizedRunType = (this.selectedStudy?.runType ?? '').replace(/\s+/g, '').toLowerCase();
      const isCalibratorAdjustment = normalizedRunType === 'calibratoradjustment';
      const hasPresetAssignments = !!this.selectedStudy?.adjustedCalLots?.valueAssignment?.length;

      if (isCalibratorAdjustment && hasPresetAssignments) {
        this.store.dispatch(setAdjustedCalLots({ adjustedCalLots: this.selectedStudy.adjustedCalLots }));
      } else {
        const valueAssignment = getValueAssignmentData(this.reportContentItemsData, this.adjustedCalLotData);
        this.store.dispatch(setAdjustedCalLots({ adjustedCalLots: valueAssignment }));
      }
    }


    this.actions.pipe(
      filter(action => action.type === runCalculationSuccess.type
        || action.type === runCalculationError.type 
      ),
      take(1)
    ).subscribe(() => {
      this.store.dispatch(setProcessingStatus({ isProcessing: false }));
    });

    const calculationRequest = new CalculationRequest(this.selectedStudy, this.panelName, label);

    // Dispatch new runCalculation action
    if (label === 'GetCsv') {
      this.store.dispatch(runGetCalculationCsv({
        calculationRequest: calculationRequest
      }));
    }
    else  {
      this.store.dispatch(runCalculation({
        calculationRequest: calculationRequest
      }));
    }
  }

  openStatusSummaryDialog(): void {
    this.dialog.open(StatusSummaryDialogComponent, {
      autoFocus: false,
      panelClass: ['bpx-dialog-container', 'status-summary-dialog-container'],
      width: '90vw',
      maxWidth: '1200px'
    });
  }

  /**
   * Toggles the side navigation bar
   */
  toggleSideNav() {
    if (this.sideNav) {
      this.sideNav.toggle()
    }
  }

  /** Returns true when the route title is inside the disabled list */
public isDisabledRoute(route: Route): boolean {
  const title = route && (route as any).title;
  return typeof title === 'string' && this.disabledRoutes.includes(title);
}

/**
 * Return a valid routerLink target:
 * - If the route is disabled, return null (so no navigation)
 * - If route.path is a string, return it
 * - Otherwise return null
 */
public getRouterLink(route: Route): string | null {
  const path = route && (route as any).path;
  if (this.isDisabledRoute(route)) return null;
  return typeof path === 'string' ? path : null;
}


/**
 * Click guard called from template. Prevents navigation / sidenav toggle when disabled.
 */
public onMenuClick(event: Event, route: Route) {
  this.isRouteVisible(route).pipe(take(1)).subscribe((visible) => {
    if (!visible) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  });
}

  public isEffectivelyDisabled(route: Route): Observable<boolean> {
    // true when route is not visible AND it is in disabledRoutes list
    return this.isRouteVisible(route).pipe(
      map(visible => !visible && this.isDisabledRoute(route))
    );
  }

  private openSampleDataDialog(force = false): void {
    if (this.sampleDataDialogRef) {
      return;
    }
    if (!force && this.sampleDataLoadingService.hasUserDismissed()) {
      return;
    }
    this.sampleDataDialogRef = this.dialog.getDialogById(SAMPLE_DATA_LOADING_DIALOG_ID) ??
      this.dialog.open(SampleDataLoadingDialogComponent, {
        id: SAMPLE_DATA_LOADING_DIALOG_ID,
        width: '360px',
        disableClose: true,
        hasBackdrop: false
      });
    this.sampleDataDialogRef.afterClosed().subscribe(() => {
      this.sampleDataDialogRef = null;
    });
  }

  private closeSampleDataDialog(): void {
    const activeDialog = this.sampleDataDialogRef ?? this.dialog.getDialogById(SAMPLE_DATA_LOADING_DIALOG_ID);
    activeDialog?.close();
    this.sampleDataDialogRef = null;
  }


  /**
   * Destroys
   */
  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.closeSampleDataDialog();
  }
}
